import React, { useCallback, useEffect, useState } from "react";
import {
  ArrayField,
  BooleanField,
  Datagrid,
  DateField,
  FunctionField,
  NumberField,
  ReferenceField,
  SimpleShowLayout,
  TextField,
  useNotify,
} from "react-admin";
import { Grid, useMediaQuery } from "@mui/material";
import { settings } from "app";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import dataProvider from "server/dataProvider";
import UserField from "server/components/Fields/UserField";
import { studentRequests } from "api/studentApi";
import SavePracticeResultDialog from "./SavePracticeResultDialog";
import PracticeResultHistoryDialog from "./PracticeResultHistoryDialog";

const PracticesData = ({ educationId, curriculumId }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const notify = useNotify();
  const [practiceResults, setPracticeResults] = useState([]);

  const fetchPracticeResults = useCallback(async () => {
    if (!educationId) return;
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentPracticeResults(educationId),
        null,
        "GET",
      );
      setPracticeResults(data ?? []);
    } catch (err) {
      notify("Резултатите от учебни практики не можаха да бъдат заредени. Опитайте да презаредите страницата.", {
        type: "error",
      });
      setPracticeResults([]);
    }
  }, [educationId, notify]);

  useEffect(() => {
    fetchPracticeResults();
  }, [fetchPracticeResults]);

  return (
    <SimpleShowLayout record={{ practiceResults }}>
      <Grid
        container
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <ArrayField source="practiceResults">
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              sx={{ margin: 2, ...settings.style.secondaryDatagrid }}
            >
              <ReferenceField
                source="practiceId"
                reference={studentRequests.getCurriculumPracticesSelect(curriculumId)}
                label="Учебна практика"
                link={false}
                sortable={false}
                emptyText="-"
              >
                <TextField
                  source="name"
                  emptyText="-"
                />
              </ReferenceField>
              <NumberField
                source="semester"
                label="Семестър"
                textAlign="left"
                emptyText="-"
                sortable={false}
              />
              <BooleanField
                source="completed"
                label="Завършена"
                sortable={false}
              />
              <NumberField
                source="grade"
                label="Оценка"
                textAlign="left"
                emptyText="-"
                sortable={false}
              />
              <UserField
                source="createdByUserId"
                label="Въведено от"
              />
              <DateField
                source="createdOn"
                label="Дата на въвеждане"
                locales="bg-BG"
                showTime
                sortable={false}
              />
              <SecuredComponent permission={permissions.canManageStudentsAllDisciplinesGrades}>
                <FunctionField
                  label=""
                  render={result => (
                    <SavePracticeResultDialog
                      educationId={educationId}
                      curriculumId={curriculumId}
                      practiceResult={result}
                      fetchPracticeResults={fetchPracticeResults}
                    />
                  )}
                />
              </SecuredComponent>
              <FunctionField
                label=""
                render={result => (
                  <PracticeResultHistoryDialog
                    educationId={educationId}
                    practiceId={result.practiceId}
                    semester={result.semester}
                  />
                )}
              />
            </Datagrid>
          </ArrayField>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{ ml: 2 }}
        >
          <SecuredComponent permission={permissions.canManageStudentsAllDisciplinesGrades}>
            <SavePracticeResultDialog
              educationId={educationId}
              curriculumId={curriculumId}
              practiceResults={practiceResults}
              fetchPracticeResults={fetchPracticeResults}
            />
          </SecuredComponent>
        </Grid>
      </Grid>
    </SimpleShowLayout>
  );
};

export default PracticesData;
